import { Box, Flex, IconArrowRight, IconExit, IconUserGroup, ImageButton, SortButton, Tooltip, toast } from '@gmg/gmg-react-components';
import { FC, MouseEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppContext from 'src/AppContext';
import SearchBox from 'src/components/shared/SearchBox';
import { getErrorMsgForToast } from 'src/components/toastErrorHandler';
import InvitationStatusBadge, { InvitationStatus } from 'src/components/userManagement/InvitationStatusBadge';
import useFormatter from 'src/customHooks/useFormatter';
import useSort from 'src/customHooks/useSort';
import { useCloseUserSessions } from 'src/graphql/useCloseUserSessions';
import { useGetAllUsers } from 'src/graphql/useGetAllUsers';
import { organizationPaths } from 'src/routes';
import { SortingOrder, Table, TableContainer, TableContainerCardStyle, Thead, Tr } from 'src/shared/TableStyle';
import WrappedSpinner from 'src/shared/WrappedSpinner';
import BreadCrumbItem from '../shared/BreadCrumbItem';

const AllUsersContainer: FC<{}> = () => {
    const { t } = useTranslation();
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const { formatLastActivityDate, formatDateWithTime } = useFormatter();
    const [filterValue, setFilterValue] = useState<string>('');
    const [sortingStatus, setSortingStatus] = useState<SortingOrder>({ direction: 'ASC', propertyName: 'name' });
    const { getSortFn } = useSort();
    const sortFn = getSortFn(sortingStatus.propertyName, sortingStatus.direction);
    const closeUserSessions = useCloseUserSessions();

    const { data: userList, error, isLoading } = useGetAllUsers();

    useEffect(() => {
        if (!error) {
            return;
        }
        context.onError(error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    const filteredUsers = filterValue.trim()
        // If we also want to filter by the user role, we need to map the role enum to the localized string.
        ? userList?.filter(({ email, firstName, lastName, orgName }) => {
            const fields = [email, firstName, lastName, orgName];
            const lowerCaseFilter = filterValue.trim().toLowerCase();
            return fields.some(field => field?.toLowerCase().includes(lowerCaseFilter));
        })
        : userList; // If filterValue is empty, return the full list.

    filteredUsers?.sort(sortFn);

    const handleSortButtonClick = (label: string) => {
        setSortingStatus({
            direction: sortingStatus.direction === 'ASC' ? 'DESC' : 'ASC',
            propertyName: label,
        });
    };

    const handleRemoveActiveSession = (userId: string, orgId: string) => {
        void toast.promise(closeUserSessions.mutateAsync({ userId, orgId }), {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: t(
                'AllUsersList.removeActiveSession.toast_success',
                'The active user session was removed successfully.',
            ),
            error: (response) => getErrorMsgForToast(response, t),
        });
    };

    const handleRemoveActiveSessionIconClick = (ev: MouseEvent, userId: string, orgId: string) => {
        ev.stopPropagation();

        context.onShowModal('infoModal', {
            title: t('AllUsersList.removeActiveSession.modal_title', 'Remove active user session'),
            content: (
                <Box maxWidth='480px'>
                    <Box>
                        <span className="px10_400_normal">
                            {t(
                                'AllUsersList.removeActiveSession.question_desc',
                                'You are about to remove this user\'s active session. This action should be only used in exceptional cases and will be logged with your name. Are you sure you want to remove this active session?',
                            )}
                        </span>
                    </Box>
                </Box>
            ),
            onOkClick: () => handleRemoveActiveSession(userId, orgId),
            btnOkCaption: t('AllUsersList.removeActiveSession.confirm_btn', 'Confirm'),
        });
    };

    return (
        <Flex flexDirection="column" gap="2px" bg="background">
            <Flex width="100%" height="64px" gap="2px" bg="background">
                <Flex width="100%" height="100%" justifyContent="space-between" alignItems="center" bg="backgroundPaper" pr="24px" pl="24px">
                    <BreadCrumbItem
                        label={t('HeaderPageTitles.all_users_title', 'User Management')}
                        icon={<IconUserGroup width={16} height={16}/>}
                        isHighlighted
                    />
                    <SearchBox
                        onChange={(e) => setFilterValue(e.target.value)}
                        value={filterValue}
                    />
                </Flex>
            </Flex>

            <TableContainer>
                {isLoading
                    ? <WrappedSpinner label={t('Common.Spinner', 'Loading...')} labelPosition="bottom" />
                    : <TableContainerCardStyle aria-label='TableContainerCardStyle' className="vertical-scrollbar">
                        <Table aria-label="all-users-table">
                            <Thead>
                                <Tr isSelected={false} isHeaderRow>
                                    <th className="px12_600_normal">
                                        <SortButton
                                            buttonLabel={t('AllUsersList.tableLabels.user_title', {
                                                defaultValue: 'User (total number: {{totalNumber}})',
                                                totalNumber: userList?.length || '0',
                                            })}
                                            onClick={() => handleSortButtonClick('firstName')}
                                            direction={sortingStatus.propertyName === 'firstName' ? sortingStatus.direction : undefined}
                                        />
                                    </th>
                                    <th className="px12_600_normal">
                                        <SortButton
                                            buttonLabel={t('UserManagement.tableLabels.roles_title', 'Role')}
                                            onClick={() => handleSortButtonClick('role')}
                                            direction={sortingStatus.propertyName === 'role' ? sortingStatus.direction : undefined}
                                        />
                                    </th>
                                    <th className="px12_600_normal">
                                        <SortButton
                                            buttonLabel={t('UserManagement.tableLabels.organization_name_title', 'Organization name')}
                                            onClick={() => handleSortButtonClick('orgName')}
                                            direction={sortingStatus.propertyName === 'orgName' ? sortingStatus.direction : undefined}
                                        />
                                    </th>
                                    <th className="px12_600_normal">
                                        <span>{t('UserManagement.tableLabels.last_seen_title', 'Last seen')}</span>
                                    </th>
                                    <th>
                                        {/* The empty th is to fill the last item's place in the thead 'cos we have an extra td for action */}
                                    </th>
                                </Tr>
                            </Thead>
                            <tbody>
                                {filteredUsers?.map(user => (
                                    <Tr
                                        key={user.id}
                                        data-testid="row"
                                        isSelected={false}
                                        onClick={() => navigate(organizationPaths.users(user.orgId!))}
                                        isClickable
                                    >
                                        <td className="px12_400_normal">
                                            <Flex gap='5px' flexDirection='column'>
                                                <Box>
                                                    {user.invite
                                                        ? <InvitationStatusBadge status={(user.invite ? user.invite.status : 'ACCEPTED') as InvitationStatus} />
                                                        : user.firstName} {user.lastName}
                                                </Box>
                                                <span className="px10_400_weak">{user.email}</span>
                                            </Flex>
                                        </td>
                                        <td className="px12_400_normal">
                                            {t(`Roles.${user.role}_label`, user.role!)}
                                        </td>
                                        <td className="px12_400_normal">
                                            {user.orgName || '-'}
                                        </td>

                                        <td className="px12_400_normal">
                                            <Tooltip message={formatDateWithTime(user.lastActivityDate)}>
                                                <span>{formatLastActivityDate(user.lastActivityDate)}</span>
                                            </Tooltip>

                                        </td>
                                        <td className="actions">
                                            <Flex className="invisibleSVG" justifyContent='flex-end' gap='16px'>
                                                <Tooltip message={t('AllUsersList.removeActiveSession.tooltip', 'Remove active user session')}>
                                                    <ImageButton
                                                        icon={<IconExit />}
                                                        disabled={!user.id || !user.orgId}
                                                        onClick={(ev) => {
                                                            ev.stopPropagation();
                                                            if (user.id && user.orgId) {
                                                                handleRemoveActiveSessionIconClick(ev, user.id, user.orgId);
                                                            }
                                                        }}
                                                        aria-label={`Remove active session for ${user.email}`}
                                                    />
                                                </Tooltip>
                                                <ImageButton
                                                    icon={<IconArrowRight />}
                                                    aria-label={`Go to ${user.email}'s organization`}
                                                />
                                            </Flex>
                                        </td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableContainerCardStyle>
                }
            </TableContainer>
        </Flex>
    );
};

export default AllUsersContainer;
